<template>
  <v-dialog
    v-model="dialog"
    @keydown.esc="onDialogClose"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
  >
    <v-row>
      <v-snackbar
        v-model="showSnackbar"
        :color="snackbarColor"
        :timeout="snackbarTimeout"
        :top="true"
      >
        {{ snackbarMsg }}
        <v-btn dark text @click="showSnackbar = false">CLOSE</v-btn>
      </v-snackbar>

      <!-- warning dialog -->
      <v-dialog v-model="dialogRemoveWarning" max-width="400">
        <v-card>
          <v-card-title class="dialog-header-title">Attention!!!</v-card-title>
          <v-card-text>
            <v-alert outlined type="warning" prominent border="left"
              >Are you sure you want to delete this contact information?</v-alert
            >
          </v-card-text>

          <v-card-actions>
            <v-row justify="center">
              <v-btn
                class="mx-7 font-weight-bold"
                color="error"
                tile
                depressed
                dark
                @click="dialogRemoveWarning = false"
                >NO</v-btn
              >
              <v-btn
                class="mx-7 font-weight-bold"
                color="primary"
                tile
                depressed
                @click="deleteContact"
                >YES</v-btn
              >
            </v-row>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- End warning dialog -->

      <!-- Edit or add new contact dialog -->
      <v-dialog v-model="dialogContact">
        <v-row no-gutters>
          <v-col>
            <v-card flat v-if="selectedContact != null" color="white">
              <v-card-title class="dialog-header-title">{{
                newContact ? "Add new contact" : "Edit contact"
              }}</v-card-title>

              <v-card-text>
                <contact-info-layout v-model="selectedContact"></contact-info-layout>
              </v-card-text>

              <v-divider></v-divider>

              <v-card-actions>
                <v-row justify="end">
                  <v-btn
                    class="font-weight-bold"
                    tile
                    depressed
                    color="primary"
                    @click="onSaveContact"
                    >SAVE</v-btn
                  >
                  <v-btn
                    class="font-weight-bold"
                    tile
                    depressed
                    color="error"
                    @click="onCloseContactDialog"
                    >CLOSE</v-btn
                  >
                </v-row>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-dialog>
      <!-- end contact dialog -->

      <!-- Dialog for confirming contact save -->
      <v-dialog v-model="saveContactAsDialog" width="300px" persistent>
        <v-card v-if="selectedContact != null">
          <v-card-text>
            <v-row>
              <v-col>
                <span>Save contact as:</span>
                <input class="form-control" v-model="selectedContact.tag" />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-row no-gutters justify="center">
              <v-btn tile depressed dark color="primary" @click="confirmSaveContact"
                >SAVE</v-btn
              >
              <v-btn
                color="error"
                tile
                depressed
                dark
                @click="saveContactAsDialog = false"
                >CANCEL</v-btn
              >
            </v-row>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-col cols="12">
        <v-row no-gutters>
          <v-col>
            <v-card flat>
              <v-card-title>
                <v-row no-gutters>
                  <v-col cols="6" class="d-flex justify-start">
                    <span class="headline primary--text ml-3"
                      >{{ company.name }} contact list</span
                    >
                  </v-col>
                  <v-col cols="6" class="d-flex justify-end">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon
                          v-on="on"
                          class="pr-2"
                          color="primary"
                          large
                          @click="onDialogClose"
                          >mdi-close</v-icon
                        >
                      </template>
                      <span>Close this dialog</span>
                    </v-tooltip>
                  </v-col>
                </v-row>
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col
                    v-for="(contact, i) in contactList"
                    :key="i"
                    cols="12"
                    sm="6"
                    md="3"
                    lg="2"
                  >
                    <v-card tile :hover="assignable">
                      <v-card-title>
                        <v-row no-gutters>
                          <v-col class="d-flex justify-start">
                            <v-tooltip top>
                              <template v-slot:activator="{ on }">
                                <span v-on="on" class="headline text-truncate">{{
                                  contact.company_contact_name
                                }}</span>
                              </template>
                              <span>{{ contact.company_contact_name }}</span>
                            </v-tooltip>
                          </v-col>
                        </v-row>
                        <v-row v-if="contact.tag != null" no-gutters>
                          <v-col class="d-flex justify-start">
                            <v-chip
                              color="teal"
                              label
                              text-color="white"
                              class="font-weight-medium"
                              small
                            >
                              <v-icon left>label</v-icon>
                              {{ contact.tag }}
                            </v-chip>
                          </v-col>
                        </v-row>
                      </v-card-title>

                      <v-card-text @click="onContactSelect(contact)">
                        <v-row no-gutters>
                          <v-col>
                            <span class="caption text-truncate">{{
                              `${contact.contact_info.first_name} ${contact.contact_info.last_name}`
                            }}</span>
                          </v-col>
                        </v-row>
                        <v-row no-gutters>
                          <v-col>
                            <span class="caption text-truncate">{{
                              `${contact.address.line_1 || ""}`
                            }}</span>
                          </v-col>
                        </v-row>
                        <v-row no-gutters>
                          <v-col>
                            <span class="caption text-truncate">{{
                              `${contact.address.city || ""} ${
                                contact.address.country || ""
                              }`
                            }}</span>
                          </v-col>
                        </v-row>
                      </v-card-text>

                      <!-- <v-divider></v-divider> -->

                      <v-card-actions class="pt-0 mt-0">
                        <v-row justify="end" no-gutters>
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-icon
                                v-on="on"
                                class="pr-2"
                                color="primary"
                                @click="onContactEdit(contact)"
                                >mdi-pencil</v-icon
                              >
                            </template>
                            <span>Edit contact</span>
                          </v-tooltip>
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-icon
                                v-on="on"
                                class="pl-2"
                                color="error"
                                @click="onContactDelete(contact)"
                                >mdi-delete-forever</v-icon
                              >
                            </template>
                            <span>Delete contact</span>
                          </v-tooltip>
                        </v-row>
                      </v-card-actions>
                    </v-card>
                  </v-col>

                  <v-col cols="12" sm="6" md="4" lg="3">
                    <v-card
                      hover
                      color="grey lighten-3"
                      tile
                      class="d-flex justify-center mt-10"
                      height="150"
                      width="150"
                      @click="onAddContact"
                    >
                      <v-icon class="d-flex align-center" x-large>mdi-plus</v-icon>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-dialog>
</template>

<script>
import constant from "../../const";
import ContactInfoLayout from "@/components/shared/ContactInfoLayout.vue";

export default {
  props: ["dialog", "company", "assignable"],
  components: {
    ContactInfoLayout,
  },

  data() {
    return {
      snackbarColor: "",
      showSnackbar: false,
      snackbarMsg: "",
      snackbarTimeout: 6000,

      contactList: [],
      selectedContact: null,

      newContact: false,

      dialogRemoveWarning: false,
      dialogContact: false,
      saveContactAsDialog: false,

      default_contact: {
        company_contact_id: null,
        company_contact_name: null,
        company_id: this.company.company_id,
        tag: null,
        contact_info: {
          first_name: null,
          last_name: null,
          email: null,
          phone: null,
          notify: false,
        },
        address: {
          line_1: null,
          line_2: null,
          line_3: null,
          city: null,
          postcode: null,
          state: null,
          code_country: null,
          country: null,
        },
      },
    };
  },

  mounted() {},

  watch: {
    dialog() {
      this.contactList = [];
      this.selectedContact = null;

      this.newContact = false;

      this.loadContactList();
    },
  },

  methods: {
    /**
     * Load company contact list
     */
    async loadContactList() {
      this.$root.blockUIshow = true;
      this.$root.blockUImsg = "loading contacts...";

      let resp = await this.$https.get(
        `/api/company/${this.company.company_id}/contacts`
      );
      if (resp.data.status == constant.RESPONSE_STATUS_OK) {
        this.contactList = resp.data.data;
      }
      this.$root.blockUIshow = false;
    },

    /**
     * On selecting a contact to edit
     * @param {object} contactInfo // contact object
     */
    async onContactEdit(contactInfo) {
      this.$root.blockUIshow = true;
      this.$root.blockUImsg = "loading contact...";

      let resp = await this.$https.get(
        `/api/company/contacts/${contactInfo.company_contact_id}`
      );

      if (resp.data.status == constant.RESPONSE_STATUS_OK) {
        this.selectedContact = resp.data.data;
        this.newContact = false;
        this.dialogContact = true;
      } else {
        this.showSnackbar = true;
        this.snackbarColor = "error";
        this.snackbarMsg = resp.data.msg;
      }
      this.$root.blockUIshow = false;
    },

    /**
     * On selecting a contact to edit
     */
    onAddContact() {
      this.selectedContact = this.default_contact;

      this.newContact = true;
      this.dialogContact = true;
    },

    /**
     * On selecting a contact to delete,
     * opent the warning dialog
     * @param {object} contactInfo // contact object
     */
    onContactDelete(contactInfo) {
      this.selectedContact = contactInfo;
      this.dialogRemoveWarning = true;
    },

    /**
     * On selecting a contact to delete
     */
    async deleteContact() {
      this.$root.blockUIshow = true;
      this.$root.blockUImsg = "deleting contact...";

      let resp = await this.$https.delete(
        `/api/company/contacts/${this.selectedContact.company_contact_id}`
      );

      if (resp.data.status == constant.RESPONSE_STATUS_OK) {
        this.dialogRemoveWarning = false;
        this.selectedContact = null;
        await this.loadContactList();
      }
      this.$root.blockUIshow = false;
    },

    /**
     * On Save contact information open dialog
     */
    onSaveContact() {
      if (this.selectedContact.company_contact_name == null) {
        this.showSnackbar = true;
        this.snackbarColor = "error";
        this.snackbarMsg = "Please provide a company name";
        return;
      }
      this.saveContactAsDialog = true;
    },
    /**
     * confirm save contact information
     */
    async confirmSaveContact() {
      this.$root.blockUIshow = true;
      this.$root.blockUImsg = "Saving contact...";

      // create a new contact
      if (this.selectedContact.company_contact_id == null) {
        let resp = await this.$https.post(
          `/api/company/${this.company.company_id}/contacts`,
          this.selectedContact
        );
        if (resp.data.status == constant.RESPONSE_STATUS_OK) {
          this.saveContactAsDialog = false;
          this.selectedContact = null;
          this.dialogContact = false;

          this.showSnackbar = true;
          this.snackbarColor = "success";
          this.snackbarMsg = resp.data.msg;

          this.loadContactList();
        } else {
          this.showSnackbar = true;
          this.snackbarColor = "error";
          this.snackbarMsg = resp.data.msg;
        }
        this.$root.blockUIshow = false;
      } else {
        // update an existing contact
        let resp = await this.$https.put(
          `/api/company/contacts/${this.selectedContact.company_contact_id}`,
          this.selectedContact
        );

        if (resp.data.status == constant.RESPONSE_STATUS_OK) {
          this.saveContactAsDialog = false;
          this.selectedContact = null;
          this.dialogContact = false;

          this.showSnackbar = true;
          this.snackbarColor = "success";
          this.snackbarMsg = resp.data.msg;

          this.loadContactList();
        } else {
          this.showSnackbar = true;
          this.snackbarColor = "error";
          this.snackbarMsg = resp.data.msg;
        }

        this.$root.blockUIshow = false;
      }
      this.$root.blockUIshow = false;
    },

    /**
     * On selecting a contact
     * @param {object} contactInfo // contact object
     */
    onContactSelect(contactInfo) {
      if (this.assignable) {
        this.$emit("dialog-company-contact-list-close", contactInfo);
      }
    },

    /**
     * On dialog close
     */
    onDialogClose() {
      this.$emit("dialog-company-contact-list-close", null);
    },

    onCloseContactDialog() {
      this.selectedContact = null;
      this.dialogContact = false;
    },
  },
};
</script>

<style>
/* .v-card__text {
  padding: 10px;
}

.v-list__tile__content {
  height: 50px;
}
.v-list__tile__title {
  margin: 3px 0px;
}
.v-list__tile__sub-title {
  padding: 3px 0px;
} */
</style>
